/** Shared helpers for topic diagnostic “supporting pages” lists (Quick Compare + URL detail). */

import { pageEntities, sameEntity } from './page-entity';

export type TopicPageRole = 'main' | 'supporting';

export interface TopicSupportingPage {
  url: string;
  seoIntentScore: number;
  estimatedOrganicTraffic?: number;
}

export interface TopicPageChip {
  topicSlug: string;
  topicLabel: string;
  role: TopicPageRole;
}

export interface DomainTopicUrlRow {
  url: string;
  seoIntentScore: number;
  /** Focal IDF-match vs the analysis URL (FW-URL-05); primary rank when present. */
  focalMatchScore?: number;
  estimatedOrganicTraffic?: number;
  topics: TopicPageChip[];
  /** v1: best token overlap vs your site (competitor rows only). */
  similarOnYourSite?: { url: string; score: number };
}

export interface TopicSlugLabel {
  slug: string;
  label: string;
}

/** Minimal parent-topic row for slug resolution (domain topics compact API). */
export interface TopicParentLookup {
  parentTopicSlug: string;
  childTopicSlugs?: string[];
}

export interface ExtractTopicTaggedPagesOptions {
  /** Page-level topic slugs to keep; matches diagnostic bucket `parentTopicSlug` (child slug). */
  targetTopicSlugs: Iterable<string>;
  /** Preferred chip labels from page signals, keyed by normalized slug. */
  topicLabelsBySlug?: Map<string, string>;
  maxTotalPages?: number;
}

interface TopicTaggedPage extends TopicSupportingPage {
  topicSlug: string;
  topicLabel: string;
  role: TopicPageRole;
}

const DEFAULT_MIN_WEIGHT = 0.3;
const DEFAULT_TOP_N = 3;
const DEFAULT_MAX_PAGES_PER_DIAGNOSTIC = 25;

/**
 * Soft pool gate: minimum SEO intent score (`seoIntentScore` from topic diagnostics)
 * for a URL to enter supporting-pages candidates. Rows must be **strictly greater**
 * than this value. Final rank/trim/hero use `focalMatchScore` (FW-URL-05).
 */
export const SUPPORTING_PAGE_MIN_RELEVANCE_SCORE = 0.55;

/** Supporting-page rows shown per domain before “Show more pages” (dev view). */
export const SUPPORTING_PAGE_INITIAL_VISIBLE_COUNT = 5;

/** Supporting-page rows per domain column in user view before card expand. */
export const SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT = 3;

/**
 * Primary sort for supporting rows: focalMatchScore when present (FW-URL-05),
 * else seoIntentScore. Tie-break seoIntentScore.
 */
export function compareSupportingPagesByRelevance(
  a: { focalMatchScore?: number; seoIntentScore: number },
  b: { focalMatchScore?: number; seoIntentScore: number }
): number {
  const aPrimary = Number.isFinite(a.focalMatchScore) ? (a.focalMatchScore as number) : a.seoIntentScore;
  const bPrimary = Number.isFinite(b.focalMatchScore) ? (b.focalMatchScore as number) : b.seoIntentScore;
  if (bPrimary !== aPrimary) return bPrimary - aPrimary;
  return b.seoIntentScore - a.seoIntentScore;
}

export function normalizeTopicSlug(slug: string): string {
  return slug.toLowerCase().trim();
}

/** Map a page signal slug to the parent topic slug used by domain diagnostics. */
export function resolveParentTopicSlug(topicSlug: string, parentTopics: TopicParentLookup[]): string {
  const normalized = normalizeTopicSlug(topicSlug);
  for (const topic of parentTopics) {
    const parent = normalizeTopicSlug(topic.parentTopicSlug);
    if (parent === normalized) return parent;
    if (
      Array.isArray(topic.childTopicSlugs) &&
      topic.childTopicSlugs.some((child) => normalizeTopicSlug(child) === normalized)
    ) {
      return parent;
    }
  }
  return normalized;
}

/** Unique parent slugs to fetch diagnostics for (deduped). */
export function uniqueParentDiagnosticSlugs(
  pageTopics: TopicSlugLabel[],
  parentTopics: TopicParentLookup[]
): string[] {
  const parents = new Set<string>();
  for (const topic of pageTopics) {
    parents.add(resolveParentTopicSlug(topic.slug, parentTopics));
  }
  return [...parents];
}

export function buildPageTopicLabelsBySlug(pageTopics: TopicSlugLabel[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const topic of pageTopics) {
    map.set(normalizeTopicSlug(topic.slug), topic.label);
  }
  return map;
}

/** Topics on the focal page used to narrow “your site” match pools (excludes weak secondary signals). */
export const FOCAL_ANCHOR_MIN_RELATIVE_WEIGHT = 0.85;
export const FOCAL_ANCHOR_MAX_TOPICS = 2;
export const FOCAL_ANCHOR_COMPARISON_MIN_RELATIVE_WEIGHT = 0.97;

/**
 * A topic that anchors this share of a domain’s focal pages is a site-wide head
 * term, not any one page’s claim (findings §15 “ambient head-term still wins”).
 * Measured on slidespeak.co, not guessed: the two site head terms sit at 18.9%
 * (`ai-presentation-maker`) and 14.3% (`ai-presentation-platform`), and the next
 * most common anchor is 4.6% — the gap is what this floor sits in.
 */
export const AMBIENT_ANCHOR_MIN_SITE_PREVALENCE = 0.1;

/**
 * Below this share of the focal slug’s content tokens covered by the chosen
 * anchors, the anchor set is too broad for the page it anchors (findings §24:
 * “PowerPoint presentation” anchoring `how-to-record-audio-on-powerpoint`).
 */
export const ANCHOR_SLUG_COVERAGE_FLOOR = 0.6;

/**
 * Relaxed co-anchor band for a signal that covers focal-slug tokens the chosen
 * anchors miss (findings §24 “co-anchor claim/slug-specific signals even below
 * 85%”). Only slug-specific signals get this band — `recording audio` at 77.7%
 * of the top weight is the audio page’s actual claim, while `audio narration`
 * at 48.6% is a detail. The specificity gate does the work; the band only keeps
 * a long tail of weak signals out.
 */
export const FOCAL_ANCHOR_SLUG_SPECIFIC_MIN_RELATIVE_WEIGHT = 0.6;

/**
 * A rescued co-anchor must also be mostly *about* the slug — this share of its
 * own words have to appear in it. Adding a slug word is not enough on its own:
 * `how-to-record-a-presentation-on-google-slides` mentions Vimeo, Loom and
 * Screencastify, and "Vimeo Record" would otherwise be rescued for supplying
 * "record" while anchoring the pool on a third-party brand. "recording audio"
 * on the audio page scores 1.0 here; "Vimeo Record" scores 0.5.
 */
export const FOCAL_ANCHOR_RESCUE_MIN_LABEL_IN_SLUG = 0.6;

/** Path segments that carry no topic meaning, so they never count as slug content. */
const SLUG_PATH_NOISE = new Set(['blog', 'free', 'tools', 'free-tools', 'comparison', 'category', 'en', 'www']);
const SLUG_STOP_WORDS = new Set(['a', 'an', 'the', 'to', 'on', 'in', 'of', 'for', 'from', 'with', 'and', 'how', 'your']);

/** Content tokens from a URL slug, minus structural path noise. */
export function slugContentTokens(url: string): Set<string> {
  let pathname = '';
  try {
    pathname = new URL(url).pathname;
  } catch {
    pathname = url;
  }
  const out = new Set<string>();
  for (const seg of pathname.split('/')) {
    const s = seg.trim().toLowerCase();
    if (!s || SLUG_PATH_NOISE.has(s)) continue;
    for (const t of s.split(/[-_]+/)) {
      if (t.length >= 3 && !SLUG_STOP_WORDS.has(t)) out.add(t);
    }
  }
  return out;
}

/**
 * Loose stem so "recording"/"record", "songs"/"song" and "slides"/"slide"
 * collapse to the same key. Trailing `e` goes too, or "slides"→"slid" would
 * fail to match "slide".
 */
export function stemTopicToken(token: string): string {
  return token
    .toLowerCase()
    .replace(/(ing|ers|ies|es|er|s)$/i, '')
    .replace(/e$/i, '');
}

/** Same word family: equal stems, or one stem prefixes the other ("delet" ⊂ "deletion"). */
export function sameWordFamily(a: string, b: string): boolean {
  if (a === b) return true;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  return short.length >= 4 && long.startsWith(short);
}

/** Share of `target` tokens that some token in `covering` belongs to the word family of. */
export function tokensCoverTargets(covering: Iterable<string>, target: Set<string>): number {
  if (!target.size) return 1;
  const pool = [...covering].map(stemTopicToken);
  let hit = 0;
  for (const t of target) {
    const s = stemTopicToken(t);
    if (pool.some((p) => sameWordFamily(p, s))) hit += 1;
  }
  return hit / target.size;
}

/** Word tokens of a topic label, for slug-coverage comparisons. */
function topicLabelTokens(label: string): string[] {
  return label.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

/** Share of the focal slug's content tokens that a set of anchor labels covers. */
export function anchorSlugCoverage(anchorLabels: string[], focalUrl: string): number {
  return tokensCoverTargets(anchorLabels.flatMap(topicLabelTokens), slugContentTokens(focalUrl));
}

/**
 * Category / tag / section-index URLs. They are listings, not peers — as a
 * supporting page they tell the operator nothing they can act on
 * (findings §15 direction 4 / ISS001-18, `/blog/category/word` under Copilot).
 */
export function isHubUrl(url: string): boolean {
  const u = (url || '').toLowerCase();
  return /\/category\//.test(u) || /\/tag\//.test(u) || /\/(blog|free-tools|tools)\/?$/.test(u);
}

/**
 * Drop hub/category rows from your own supporting-page columns. Competitor
 * columns keep theirs — a rival's hub page is still evidence of what they
 * cover, while your own is a listing you cannot link to as a peer.
 *
 * Applied on read so snapshots built before this landed are cleaned too.
 */
export function filterHubRowsFromOwnColumns<
  T extends { isYourSite: boolean; rows: Array<{ url: string }> }
>(domains: T[]): T[] {
  return domains.map((domain) => {
    if (!domain.isYourSite || !domain.rows.length) return domain;
    const rows = domain.rows.filter((row) => !isHubUrl(String(row.url)));
    return rows.length === domain.rows.length ? domain : { ...domain, rows };
  });
}

const COMPARISON_URL_MARKERS = [
  ' vs ',
  '-vs-',
  '/vs-',
  '-versus-',
  '/versus',
  ' versus ',
  '-alternatives',
  '/alternatives',
  '-alternative/',
  '/alternative/',
  'alternatives-to',
  '-alternatives-to',
  'comparison',
  'compare ',
  '-compare-',
  '/compare/'
] as const;

export function isComparisonLikeUrlForTopicAnchors(url: string): boolean {
  const value = (url || '').toLowerCase();
  return COMPARISON_URL_MARKERS.some((marker) => value.includes(marker));
}

export interface FocalAnchorOptions {
  minRelativeWeight?: number;
  maxTopics?: number;
  /**
   * Focal URL. Enables the slug-specificity rules (findings §24) — without it
   * there is nothing to measure an anchor's breadth against, and selection
   * falls back to weight band alone.
   */
  focalUrl?: string;
  /**
   * Topic slug → share of this domain's focal pages the topic anchors. Enables
   * ambient head-term demotion (findings §15). Absent, no anchor is treated as
   * ambient — a single page cannot tell a site head term from its own claim.
   */
  anchorSitePrevalence?: ReadonlyMap<string, number>;
}

export function getFocalAnchorTopicSlugs(
  analysis: { topic?: { topicSignals?: unknown } } | null | undefined,
  options?: FocalAnchorOptions
): { slugs: Set<string>; labels: string[]; topics: TopicSlugLabel[] } {
  const signals: Array<{ topicSlug: string; label?: string; weight: number }> = (
    Array.isArray(analysis?.topic?.topicSignals) ? analysis!.topic!.topicSignals : []
  )
    .filter((s: any) => Number.isFinite(s?.weight) && s.weight > 0 && s?.topicSlug)
    .sort((a: any, b: any) => Number(b.weight) - Number(a.weight));

  if (!signals.length) {
    return { slugs: new Set(), labels: [], topics: [] };
  }

  const topWeight = signals[0].weight;
  const minW = topWeight * (options?.minRelativeWeight ?? FOCAL_ANCHOR_MIN_RELATIVE_WEIGHT);
  const maxTopics = options?.maxTopics ?? FOCAL_ANCHOR_MAX_TOPICS;
  const prevalence = options?.anchorSitePrevalence;
  const focalUrl = options?.focalUrl;

  const labelOf = (s: { topicSlug: string; label?: string }) =>
    typeof s.label === 'string' && s.label.trim() ? s.label.trim() : String(s.topicSlug);
  const isAmbient = (s: { topicSlug: string }) =>
    !!prevalence &&
    (prevalence.get(normalizeTopicSlug(String(s.topicSlug))) ?? 0) >= AMBIENT_ANCHOR_MIN_SITE_PREVALENCE;

  const inBand = signals.filter((s) => s.weight >= minW);

  // findings §15 — a site head term co-anchoring the pool drags in every page
  // that mentions it. Drop it, but only while a page-specific anchor survives:
  // an all-ambient page has nothing better to anchor on.
  const specific = inBand.filter((s) => !isAmbient(s));
  const kept = specific.length ? specific : inBand;

  const picked = kept.slice(0, maxTopics);

  // findings §24 — the co-anchor band is a weight test, so a page whose claim
  // sits just under it anchors on the broad term alone and pools the whole
  // parent topic. When the chosen anchors do not describe the slug, admit the
  // strongest signal that covers what they miss.
  if (focalUrl) {
    const slugTokens = slugContentTokens(focalUrl);
    if (slugTokens.size) {
      const rescueMinW = topWeight * FOCAL_ANCHOR_SLUG_SPECIFIC_MIN_RELATIVE_WEIGHT;
      // Weight order, so the page's strongest unchosen signal is tried first.
      const rescuePool = signals.filter(
        (s) =>
          s.weight >= rescueMinW &&
          !isAmbient(s) &&
          tokensCoverTargets(slugTokens, new Set(topicLabelTokens(labelOf(s)))) >=
            FOCAL_ANCHOR_RESCUE_MIN_LABEL_IN_SLUG
      );
      const chosen = new Set(picked.map((s) => normalizeTopicSlug(String(s.topicSlug))));
      let coverage = tokensCoverTargets(picked.flatMap((s) => topicLabelTokens(labelOf(s))), slugTokens);

      while (picked.length < maxTopics && coverage < ANCHOR_SLUG_COVERAGE_FLOOR) {
        const candidate = rescuePool.find((s) => {
          if (chosen.has(normalizeTopicSlug(String(s.topicSlug)))) return false;
          const merged = picked.concat(s).flatMap((p) => topicLabelTokens(labelOf(p)));
          return tokensCoverTargets(merged, slugTokens) > coverage;
        });
        if (!candidate) break;
        picked.push(candidate);
        chosen.add(normalizeTopicSlug(String(candidate.topicSlug)));
        coverage = tokensCoverTargets(picked.flatMap((s) => topicLabelTokens(labelOf(s))), slugTokens);
      }
    }
  }

  const topics: TopicSlugLabel[] = picked.map((s) => ({
    slug: String(s.topicSlug),
    label: labelOf(s)
  }));
  const slugs = new Set(topics.map((t) => normalizeTopicSlug(t.slug)));
  const labels = topics.map((t) => t.label);
  return { slugs, labels, topics };
}

/**
 * Topic slug → share of a domain's analyzed pages the topic would anchor.
 * Feeds {@link AMBIENT_ANCHOR_MIN_SITE_PREVALENCE}.
 *
 * Derived from the page analyses, not from stored snapshots: a snapshot-fed
 * measurement would keep re-confirming whatever the previous build chose. The
 * plain weight band is used deliberately — measuring "site head term" must not
 * depend on the ambient demotion it feeds, or the rule would eat itself.
 */
export function computeAnchorSitePrevalence(
  pages: Array<{ url: string; analysis: { topic?: { topicSignals?: unknown } } | null | undefined }>
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const page of pages) {
    const anchor = getFocalAnchorTopicSlugs(
      page.analysis,
      isComparisonLikeUrlForTopicAnchors(page.url)
        ? { minRelativeWeight: FOCAL_ANCHOR_COMPARISON_MIN_RELATIVE_WEIGHT }
        : undefined
    );
    for (const slug of anchor.slugs) counts.set(slug, (counts.get(slug) || 0) + 1);
  }

  const total = pages.length || 1;
  const share = new Map<string, number>();
  for (const [slug, n] of counts) share.set(slug, n / total);
  return share;
}

export function rowOverlapsFocalAnchorTopics(
  row: DomainTopicUrlRow,
  anchorSlugs: Set<string>
): boolean {
  if (!anchorSlugs.size) return true;
  return row.topics.some((chip) => anchorSlugs.has(normalizeTopicSlug(chip.topicSlug)));
}

export function filterRowsByFocalAnchorTopics(
  rows: DomainTopicUrlRow[],
  anchorSlugs: Set<string>
): DomainTopicUrlRow[] {
  if (!anchorSlugs.size) return rows;
  return rows.filter((row) => rowOverlapsFocalAnchorTopics(row, anchorSlugs));
}

/**
 * FW-ENTITY-01 — a rival page about a DIFFERENT product is not a supporting
 * page for ours, however well its topic matches.
 *
 * `/blog/monday-com-competitors` and `/blog/float-alternatives` both carry
 * "resource management"; the topic sees the category and is blind to the
 * product. The owner's rule: "it's fine that 'resource management' and 'time
 * tracking' is a match when we talk about monday or float, but it's not fine
 * that 'float' is a match when we talk about 'monday'."
 *
 * True — DROP the row — only when ALL hold:
 *   1. the focal page names a product (`focalEntities` non-empty), and
 *   2. the row names a product, and
 *   3. none of the row's products is one of ours.
 * Every other combination keeps the row: a focal with no entity is not
 * entity-scoped; a row with no entity (`project-resource-management`) is about
 * the topic; a row that names OUR product (`monday-com-vs-teamwork-vs-scoro`)
 * is the half of the rule that must not be lost.
 *
 * `focalEntities` is the whole set from {@link pageEntities}, not one entity:
 * `happyfox-vs-zendesk-vs-manyrequests` is about all three, and a rival's
 * `zendesk-alternatives` belongs beside it.
 */
export function rowNamesCompetingEntity(
  row: { url: string },
  focalEntities: readonly string[]
): boolean {
  if (!focalEntities.length) return false;
  const theirs = pageEntities(row.url);
  if (!theirs.length) return false;
  return !theirs.some((entity) => focalEntities.some((ours) => sameEntity(ours, entity)));
}

/** Rows that do not name a product competing with the focal page's. */
export function filterRowsByFocalEntity<T extends { url: string }>(rows: T[], focalUrl: string): T[] {
  const focalEntities = pageEntities(focalUrl);
  if (!focalEntities.length) return rows;
  return rows.filter((row) => !rowNamesCompetingEntity(row, focalEntities));
}

/**
 * Apply {@link rowNamesCompetingEntity} to RIVAL columns only. Your own
 * `podio-alternatives` beside your `clickup-alternatives` is still a page on
 * your site about the same topic — the internal-link pool and match pool want
 * it — so own columns are left alone.
 *
 * Applied on read as well as on build (like {@link filterHubRowsFromOwnColumns})
 * so snapshots stored before this landed are cleaned too.
 */
export function filterCompetingEntityRowsFromRivalColumns<
  T extends { isYourSite: boolean; rows: Array<{ url: string }> }
>(domains: T[], focalUrl: string): T[] {
  const focalEntities = pageEntities(focalUrl);
  if (!focalEntities.length) return domains;
  return domains.map((domain) => {
    if (domain.isYourSite || !domain.rows.length) return domain;
    const rows = domain.rows.filter((row) => !rowNamesCompetingEntity(row, focalEntities));
    return rows.length === domain.rows.length ? domain : { ...domain, rows };
  });
}

/** Your-site candidates that share focal anchor + at least one topic chip with the competitor row. */
export function filterYourMatchPoolForCompetitorRow(
  yourRows: DomainTopicUrlRow[],
  competitorRow: DomainTopicUrlRow,
  anchorSlugs: Set<string>
): DomainTopicUrlRow[] {
  const anchorPool = filterRowsByFocalAnchorTopics(yourRows, anchorSlugs);
  if (!anchorPool.length) return [];

  const compSlugs = new Set(
    competitorRow.topics.map((c) => normalizeTopicSlug(c.topicSlug)).filter(Boolean)
  );
  if (!compSlugs.size) return anchorPool;

  return anchorPool.filter((row) =>
    row.topics.some((c) => compSlugs.has(normalizeTopicSlug(c.topicSlug)))
  );
}

export function getTopTopicsForSupportingPages(
  analysis: { topic?: { topicSignals?: unknown } } | null | undefined,
  options?: { minWeight?: number; limit?: number }
): TopicSlugLabel[] {
  const minWeight = options?.minWeight ?? DEFAULT_MIN_WEIGHT;
  const limit = options?.limit ?? DEFAULT_TOP_N;
  const signals: any[] = Array.isArray(analysis?.topic?.topicSignals) ? analysis!.topic!.topicSignals : [];
  return signals
    .filter((s: any) => Number.isFinite(s?.weight) && Number(s.weight) >= minWeight && s?.topicSlug)
    .sort((a: any, b: any) => Number(b.weight) - Number(a.weight))
    .slice(0, limit)
    .map((s: any) => ({
      slug: String(s.topicSlug),
      label: typeof s.label === 'string' && s.label.trim() ? s.label.trim() : String(s.topicSlug)
    }));
}

/**
 * Pages from a parent-topic diagnostic, one chip per matching child bucket (core / mention).
 */
export function extractTopicTaggedPagesFromDiagnostic(
  diagnosticResponse: unknown,
  options: ExtractTopicTaggedPagesOptions
): TopicTaggedPage[] {
  const res = diagnosticResponse as { data?: { coverage?: { directlyCoveredTopics?: unknown[] } } } | null;
  if (!res?.data?.coverage?.directlyCoveredTopics) return [];

  const targetSlugs = new Set(
    [...options.targetTopicSlugs].map((s) => normalizeTopicSlug(s)).filter(Boolean)
  );
  if (targetSlugs.size === 0) return [];

  const maxTotal = options.maxTotalPages ?? DEFAULT_MAX_PAGES_PER_DIAGNOSTIC;
  const topics: any[] = res.data.coverage.directlyCoveredTopics;
  const tagged: TopicTaggedPage[] = [];

  for (const bucket of topics) {
    const bucketSlug = normalizeTopicSlug(String(bucket?.parentTopicSlug || ''));
    if (!bucketSlug || !targetSlugs.has(bucketSlug)) continue;

    const bucketLabel =
      options.topicLabelsBySlug?.get(bucketSlug) ||
      (typeof bucket?.label === 'string' && bucket.label.trim() ? bucket.label.trim() : bucketSlug);

    const pushPage = (p: any, role: TopicPageRole) => {
      if (!p?.url) return;
      tagged.push({
        url: String(p.url),
        seoIntentScore: Number.isFinite(p.seoIntentScore) ? Number(p.seoIntentScore) : 0,
        estimatedOrganicTraffic:
          typeof p.estimatedOrganicTraffic === 'number' ? p.estimatedOrganicTraffic : undefined,
        topicSlug: bucketSlug,
        topicLabel: bucketLabel,
        role
      });
    };

    for (const p of Array.isArray(bucket.corePages) ? bucket.corePages : []) {
      pushPage(p, 'main');
    }
    for (const p of Array.isArray(bucket.mentionPages) ? bucket.mentionPages : []) {
      pushPage(p, 'supporting');
    }
  }

  return tagged.sort((a, b) => b.seoIntentScore - a.seoIntentScore).slice(0, maxTotal);
}

export function passesSupportingPageRelevanceThreshold(seoIntentScore: number): boolean {
  return Number.isFinite(seoIntentScore) && seoIntentScore > SUPPORTING_PAGE_MIN_RELEVANCE_SCORE;
}

/** Keep supporting-page rows whose relevance exceeds {@link SUPPORTING_PAGE_MIN_RELEVANCE_SCORE}. */
export function filterSupportingPagesByRelevance(rows: DomainTopicUrlRow[]): DomainTopicUrlRow[] {
  return rows.filter((row) => passesSupportingPageRelevanceThreshold(row.seoIntentScore));
}

/** One row per URL with topic chips (deduped by slug + role). */
export function mergeTopicTaggedPagesByUrl(
  pages: TopicTaggedPage[],
  maxRows?: number
): DomainTopicUrlRow[] {
  const byUrl = new Map<string, DomainTopicUrlRow>();

  for (const page of pages) {
    const key = normalizeUrlForCompare(page.url);
    let row = byUrl.get(key);
    if (!row) {
      row = {
        url: page.url,
        seoIntentScore: page.seoIntentScore,
        estimatedOrganicTraffic: page.estimatedOrganicTraffic,
        topics: []
      };
      byUrl.set(key, row);
    }

    if (page.seoIntentScore > row.seoIntentScore) {
      row.seoIntentScore = page.seoIntentScore;
      row.estimatedOrganicTraffic = page.estimatedOrganicTraffic;
    }

    const chipKey = `${page.topicSlug}::${page.role}`;
    if (!row.topics.some((c) => `${c.topicSlug}::${c.role}` === chipKey)) {
      row.topics.push({
        topicSlug: page.topicSlug,
        topicLabel: page.topicLabel,
        role: page.role
      });
    }
  }

  const sortChips = (chips: TopicPageChip[]) =>
    chips.sort((a, b) => {
      if (a.role !== b.role) return a.role === 'main' ? -1 : 1;
      return a.topicLabel.localeCompare(b.topicLabel);
    });

  const merged = [...byUrl.values()]
    .map((row) => ({ ...row, topics: sortChips(row.topics) }))
    .sort((a, b) => b.seoIntentScore - a.seoIntentScore);
  if (maxRows != null && maxRows > 0) {
    return merged.slice(0, maxRows);
  }
  return merged;
}

/** @deprecated Use extractTopicTaggedPagesFromDiagnostic + merge for URL detail ownership. */
export function extractSupportingPagesFromDiagnostic(
  diagnosticResponse: unknown,
  maxPages = 5
): TopicSupportingPage[] {
  const res = diagnosticResponse as { data?: { coverage?: { directlyCoveredTopics?: unknown[] } } } | null;
  if (!res?.data?.coverage?.directlyCoveredTopics) return [];
  const topics: any[] = res.data.coverage.directlyCoveredTopics;
  const allUrls: TopicSupportingPage[] = [];
  for (const t of topics) {
    const pages = [
      ...(Array.isArray(t.corePages) ? t.corePages : []),
      ...(Array.isArray(t.mentionPages) ? t.mentionPages : [])
    ];
    for (const p of pages) {
      if (p?.url) {
        allUrls.push({
          url: String(p.url),
          seoIntentScore: Number.isFinite(p.seoIntentScore) ? Number(p.seoIntentScore) : 0,
          estimatedOrganicTraffic:
            typeof p.estimatedOrganicTraffic === 'number' ? p.estimatedOrganicTraffic : undefined
        });
      }
    }
  }
  return allUrls.sort((a, b) => b.seoIntentScore - a.seoIntentScore).slice(0, maxPages);
}

export function formatSupportingPagePath(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.pathname || '/';
  } catch {
    return url;
  }
}

export function formatSupportingPageTraffic(v: number | undefined): string {
  if (!Number.isFinite(v) || v === undefined || v === null) return '';
  if (v >= 1000) return `~${(v / 1000).toFixed(1)}k/mo`;
  return `~${Math.round(v)}/mo`;
}

export function normalizeUrlForCompare(a: string): string {
  try {
    const u = new URL(a.trim());
    u.hash = '';
    let path = u.pathname.replace(/\/+$/, '') || '/';
    return `${u.origin}${path}`.toLowerCase();
  } catch {
    return a.trim().toLowerCase();
  }
}
